import { inject } from '@angular/core';
import { CanActivateFn, Router, UrlTree } from '@angular/router';
import { catchError, defaultIfEmpty, isObservable, map, Observable, of, switchMap, take } from 'rxjs';
import { QuoteToolLiteModeService } from '../services/modes/quote-tool-lite-mode.service';
import { RouteIds } from '../types';
import { QuoteToolStrategyService } from '@lx/quote-tool/shared/services';
import { LoggerService } from '@lx/shared/logger';

/**
 * Guard that handles navigation from the root path.
 *
 * This guard does NOT load data. All required data is loaded by APP_INITIALIZER
 * (AppInitializerService) before routing begins. It only checks the loaded state and
 * performs navigation logic based on already-loaded data.
 *
 * If data is not loaded (which should not happen in normal operation), it logs an error
 * and redirects to the default course step.
 *
 * Flow:
 * 1. Checks if data is loaded (via qtLiteModeService.loaded)
 * 2. If loaded, determines the target route and returns it
 * 3. If not loaded, logs error and returns the course step route (default)
 *
 * Error Handling:
 * - If no strategy or step is found, or observable errors: returns the course step route (default)
 *
 * @returns Observable<UrlTree> indicating the route to navigate to
 */
export const rootRedirectGuard: CanActivateFn = (): Observable<UrlTree> => {
  const qtLiteModeService = inject(QuoteToolLiteModeService);
  const logger = inject(LoggerService);
  const router = inject(Router);

  let navigation$: Observable<string> | undefined;
  if (qtLiteModeService.loaded) {
    navigation$ = handleNavigation(qtLiteModeService, logger);
  } else {
    navigation$ = handleNavigationFailure(logger, 'Data not loaded before routing, redirecting to default course step');
  }
  return navigation$.pipe(map((routeId) => router.createUrlTree([routeId])));
};

/**
 * Handles navigation logic based on already-loaded data to return the proper route id.
 *
 * @param qtLiteModeService - The mode service with loaded data
 * @param logger - Logger for error reporting
 * @returns Observable<string>
 */
const handleNavigation = (qtLiteModeService: QuoteToolLiteModeService, logger: LoggerService): Observable<string> => {
  const strategy: QuoteToolStrategyService<RouteIds> | undefined = qtLiteModeService.getStrategy();
  if (!strategy) {
    return handleNavigationFailure(logger, 'No strategy found after loading data, redirecting to default course step');
  }
  const stepToNavigate = strategy.getStepToNavigate();
  if (!stepToNavigate) {
    return handleNavigationFailure(logger, 'Strategy returned invalid step to navigate, redirecting to default course step');
  }
  if (isObservable(stepToNavigate)) {
    return stepToNavigate.pipe(
      take(1),
      defaultIfEmpty(RouteIds.course),
      switchMap((routeId: RouteIds) => {
        if (!routeId) {
          return handleNavigationFailure(logger, 'Observable emitted invalid route ID, redirecting to default course step');
        }
        return of(routeId);
      }),
      catchError((error) => handleNavigationFailure(logger, 'Error getting step to navigate from observable', error)),
    );
  } else {
    return of(stepToNavigate);
  }
};

/**
 * Handles navigation failures by logging the error and navigating to the default step.
 *
 * @param logger - The logger service for error reporting
 * @param errorMessage - The specific error message to log
 * @param error - Optional error object for detailed logging
 * @returns Observable<string> the default route ID to navigate to (course step)
 */
const handleNavigationFailure = (logger: LoggerService, errorMessage: string, error?: string | Error): Observable<string> => {
  if (error) {
    const errorDetails = typeof error === 'string' ? error : error.message || JSON.stringify(error);
    logger.error(errorMessage, errorDetails);
  } else {
    logger.error(errorMessage);
  }

  return of(RouteIds.course);
};
